import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import { RadioButtonsGroup } from '../../components/widgets';
import { SECTION_ACCESS_TYPES, VIEW_ONLY_SECTIONS } from '../../utils/consts';
import { CUSTOM_ACCESS_TYPE, translationPrefix as translationRootPrefix } from '../utils';
import AccessTypeIcon from './AccessTypeIcon';
import css from './SectionsPart.css';

const translationPrefix = `${translationRootPrefix}.MANAGE_PERMISSIONS_PAGE`;

const SectionsPart = ({ part, accessTypesRecord, selectedAccessType, onToggleAccessType, translate }) => {
    const displayName = useMemo(() => {
        let result = '';
        if (part.displayName) {
            result = translate(part.displayName);
        }

        if (!part.displayName || (part.displayName && selectedAccessType !== CUSTOM_ACCESS_TYPE)) {
            if (part.displayName) result += ' - ';

            part.sectionNames.forEach((sectionName, index) => {
                result += `${index > 0 ? ' & ' : ''} ${translate(
                    `${translationPrefix}.SECTIONS.${sectionName}.DISPLAY_NAME`
                )}`;
            });
        }
        return result;
    }, [part.displayName, part.sectionNames, selectedAccessType, translate]);

    const partAccessType = useMemo(() => {
        const isEditAccess = part.sectionNames.every(
            sectionName => accessTypesRecord[sectionName] === SECTION_ACCESS_TYPES.EDIT
        );
        return isEditAccess ? SECTION_ACCESS_TYPES.EDIT : SECTION_ACCESS_TYPES.VIEW;
    }, [part.sectionNames, accessTypesRecord]);

    const isChecked = useCallback(
        (accessType, sectionName) => {
            if (accessType === CUSTOM_ACCESS_TYPE) {
                const allEqual = part.sectionNames.every(
                    name => accessTypesRecord[name] === accessTypesRecord[part.sectionNames[0]]
                );
                return !allEqual && accessTypesRecord[sectionName] === SECTION_ACCESS_TYPES.EDIT;
            }
            return part.sectionNames.every(name => accessTypesRecord[name] === accessType);
        },
        [part.sectionNames, accessTypesRecord]
    );

    const radioItems = useMemo(() => {
        const result = [{ accessType: SECTION_ACCESS_TYPES.EDIT }];
        if (part.sectionNames.length >= 2) {
            result.push(
                ...part.sectionNames.map(sectionName => ({
                    accessType: CUSTOM_ACCESS_TYPE,
                    sectionName,
                }))
            );
        }
        result.push({ accessType: SECTION_ACCESS_TYPES.VIEW });
        return result.map(({ accessType, sectionName = '' }) => ({
            id: `${part.sectionNames.join('-')}-${sectionName}-${accessType}`,
            sectionName,
            accessType,
            label: `${translationPrefix}.SECTION_ACCESS_TYPES_CUSTOM_TITLE.${accessType}`,
            data: {
                displayName: translate(`${translationPrefix}.SECTIONS.${sectionName}.DISPLAY_NAME`),
            },
            labelClassName: classnames(css.accessTypeLabel, css.radioLabel),
            tagClassName: css.radioTag,
            checked: isChecked(accessType, sectionName),
            tag: <AccessTypeIcon accessType={accessType} size={IconSizes.X_SMALL} interactive={false} />,
        }));
    }, [part.sectionNames, isChecked, translate]);

    const handleChange = item => {
        if (item.accessType === CUSTOM_ACCESS_TYPE) {
            onToggleAccessType([item.sectionName], SECTION_ACCESS_TYPES.EDIT);
            onToggleAccessType(
                part.sectionNames.filter(sectionName => sectionName !== item.sectionName),
                SECTION_ACCESS_TYPES.VIEW
            );
        } else {
            onToggleAccessType(part.sectionNames, item.accessType);
        }
    };

    return (
        <div className={classnames(css.part, { [css.customPart]: selectedAccessType === CUSTOM_ACCESS_TYPE })}>
            {selectedAccessType !== CUSTOM_ACCESS_TYPE && (
                <div className={css.accessTypeContainer}>
                    <AccessTypeIcon accessType={partAccessType} size={IconSizes.X_SMALL} interactive={false} />
                    <span className={css.accessTypeLabel}>
                        <Translate id={`${translationPrefix}.SECTION_ACCESS_TYPES_TITLE.${partAccessType}`} />
                    </span>
                </div>
            )}
            <div className={css.partTitle}>
                <span className={css.displayName}>{displayName}</span>
                {(part.tooltipImageSrc || part.tooltipDescriptionSectionName) && (
                    <Tooltip
                        title={
                            <>
                                {part.tooltipDescriptionSectionName &&
                                    translate(
                                        `${translationPrefix}.SECTIONS.${part.tooltipDescriptionSectionName}.TOOLTIP_DESCRIPTION`
                                    )}
                                {part.tooltipImageSrc && (
                                    <img src={part.tooltipImageSrc} alt="" className={css.tooltipImage} />
                                )}
                            </>
                        }
                        wrapperClass={css.tooltipWrapper}
                    >
                        <Icon name="question-line" size={IconSizes.X_SMALL} variant={IconVariants.LIGHT} />
                    </Tooltip>
                )}
            </div>
            {selectedAccessType === CUSTOM_ACCESS_TYPE && (
                <RadioButtonsGroup
                    radioItems={radioItems}
                    itemsClassName={css.radioItem}
                    checkmarkClassName={css.radioCheckmark}
                    onChange={handleChange}
                    disabled={VIEW_ONLY_SECTIONS.some(sectionName => part.sectionNames.includes(sectionName))}
                />
            )}
        </div>
    );
};

SectionsPart.propTypes = {
    part: PropTypes.shape({
        sectionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
        displayName: PropTypes.string,
        tooltipImageSrc: PropTypes.string,
        tooltipDescriptionSectionName: PropTypes.string,
    }).isRequired,
    accessTypesRecord: PropTypes.objectOf(PropTypes.number).isRequired,
    selectedAccessType: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onToggleAccessType: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(SectionsPart);
